import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Truck,
  Clock,
  User,
  Calendar,
  DollarSign,
  Package,
  MessageSquare,
  Download,
  Trash2,
  Send,
  Reply,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  1: "bg-blue-100 text-blue-800",
  2: "bg-yellow-100 text-yellow-800",
  3: "bg-orange-100 text-orange-800",
  4: "bg-green-100 text-green-800",
  5: "bg-emerald-100 text-emerald-800",
};

const docTypeLabels = {
  invoice: "Invoice",
  proof_of_manufacturing: "Proof of Manufacturing",
  packing_slip: "Packing Slip",
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchOrder();
    fetchMessages();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error("Failed to fetch order details");
      navigate(user?.is_admin ? "/admin" : "/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/messages`);
      setMessages(response.data);
      
      // For customers, mark messages with admin replies as read
      if (!user?.is_admin) {
        const unreadMessages = response.data.filter(
          msg => msg.reply && !msg.read_by_customer
        );
        for (const msg of unreadMessages) {
          try {
            await axios.put(`${API}/customer/message-notifications/${msg.id}/dismiss`);
          } catch (error) {
            console.error("Failed to mark message as read:", error);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/orders/${orderId}/messages`, { message: newMessage });
      toast.success("Message sent to admin");
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const downloadDocument = (docId, filename) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${API}/documents/${docId}/direct-download?token=${encodeURIComponent(token)}`;
    
    fetch(downloadUrl)
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        toast.error('Failed to download document');
      });
  };

  const deleteDocument = async (docId, filename) => {
    console.log('Delete clicked:', docId, filename);
    try {
      console.log('Deleting document...');
      await axios.delete(`${API}/admin/documents/${docId}`);
      console.log('Delete successful');
      toast.success('Document deleted');
      fetchOrder();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const backPath = user?.is_admin ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={backPath}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0F766E] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                Order Details
              </h1>
              <p className="text-slate-600">{order.invoice_number}</p>
            </div>
            <Badge className={`${statusColors[order.status]} text-sm px-3 py-1`}>
              {order.status_label}
            </Badge>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Order Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0F766E]" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Patient Name</p>
                  <p className="font-medium">{order.patient_name}</p>
                </div>
              </div>
              {order.item_description && (
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Item Description</p>
                    <p className="font-medium">{order.item_description}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Date Received</p>
                  <p className="font-medium">
                    {new Date(order.date_received).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Cost</p>
                  <p className="font-medium text-lg">${order.cost.toFixed(2)}</p>
                </div>
              </div>
              {!user?.is_admin && (
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Clinic</p>
                    <p className="font-medium">{order.clinic_name}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tracking Information */}
          {order.status >= 4 && order.tracking_number && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#0F766E]" />
                  Tracking Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 mb-2">Tracking Number</p>
                  <p className="font-mono text-lg font-semibold text-green-900">
                    {order.tracking_number}
                  </p>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Use this tracking number with your shipping carrier to track your package.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          <Card className={order.status >= 4 && order.tracking_number ? "" : "md:col-span-1"}>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0F766E]" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.documents?.length > 0 ? (
                <div className="space-y-3">
                  {order.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#0F766E]" />
                        <div>
                          <p className="font-medium text-sm">
                            {docTypeLabels[doc.doc_type] || doc.doc_type}
                          </p>
                          <p className="text-xs text-slate-500">{doc.filename}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadDocument(doc.id, doc.filename)}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {user?.is_admin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteDocument(doc.id, doc.filename)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Notes */}
          <Card className={!(order.status >= 4 && order.tracking_number) ? "md:col-span-1" : ""}>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#0F766E]" />
                Order Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.notes?.length > 0 ? (
                <div className="space-y-4">
                  {order.notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span className="text-xs text-amber-700">
                          {new Date(note.created_at).toLocaleString("en-CA")}
                        </span>
                        <span className="text-xs text-amber-600 font-medium">
                          by {note.created_by}
                        </span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No notes for this order</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Messages Section - Only for customers */}
        {!user?.is_admin && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#0F766E]" />
                Messages About This Order
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Send New Message */}
              <div className="mb-6">
                <Textarea
                  placeholder="Send a message to admin about this order (e.g., request changes, ask questions)..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="mb-2"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={sendingMessage}
                  className="bg-[#0F766E] hover:bg-[#0D635C]"
                >
                  {sendingMessage ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                      Sending...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>

              {/* Message History */}
              {messages.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-slate-700">Message History</h4>
                  {messages.map((msg) => (
                    <div key={msg.id} className="border rounded-lg overflow-hidden">
                      {/* Customer's message */}
                      <div className="p-4 bg-blue-50 border-b">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">You</span>
                          <span className="text-xs text-blue-500">{msg.created_at}</span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      
                      {/* Admin's reply */}
                      {msg.reply ? (
                        <div className="p-4 bg-green-50">
                          <div className="flex items-center gap-2 mb-2">
                            <Reply className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-medium text-green-700">Admin Reply</span>
                            <span className="text-xs text-green-500">{msg.reply_at}</span>
                          </div>
                          <p className="text-slate-700 whitespace-pre-wrap">{msg.reply}</p>
                        </div>
                      ) : (
                        <div className="p-3 bg-slate-50 text-center">
                          <span className="text-xs text-slate-500">Awaiting admin response...</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500">
                  <p className="text-sm">No messages yet. Send a message if you have questions about this order.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Status Timeline */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Order Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-2 md:gap-0">
              {[
                { status: 1, label: "Received" },
                { status: 2, label: "In Production" },
                { status: 3, label: "Packaging" },
                { status: 4, label: "Shipped" },
                { status: 5, label: "Delivered" },
              ].map((step, index) => (
                <div key={step.status} className="flex-1 flex items-center">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                        order.status >= step.status
                          ? "bg-[#0F766E] text-white"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {step.status}
                    </div>
                    <p
                      className={`text-xs mt-2 text-center ${
                        order.status >= step.status ? "text-[#0F766E] font-medium" : "text-slate-500"
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                  {index < 4 && (
                    <div
                      className={`hidden md:block h-1 flex-1 mx-2 rounded ${
                        order.status > step.status ? "bg-[#0F766E]" : "bg-slate-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderDetail;
